import { useState, useMemo } from 'react';

/**
 * Filter management hook
 * Handles multiple filter types with advanced filtering logic
 */
export const useFilters = (data, filterConfigs = []) => {
  const [filters, setFilters] = useState(() => {
    const initial = {};
    filterConfigs.forEach(config => {
      initial[config.key] = config.defaultValue || 'all';
    });
    return initial;
  });

  // Apply all filters to data
  const filteredData = useMemo(() => {
    return data.filter(item => {
      return filterConfigs.every(config => {
        const filterValue = filters[config.key];

        // Skip if filter is 'all' or not set
        if (!filterValue || filterValue === 'all') return true;

        // Get the value from the item
        const itemValue = config.accessor(item);

        // Apply the filter based on type
        switch (config.type) {
          case 'exact':
            return itemValue === filterValue;
          case 'includes':
            return Array.isArray(itemValue)
              ? itemValue.includes(filterValue)
              : itemValue === filterValue;
          case 'boolean':
            return Boolean(itemValue) === Boolean(filterValue);
          case 'custom':
            return config.filterFn ? config.filterFn(item, filterValue) : true;
          default:
            return itemValue === filterValue;
        }
      });
    });
  }, [data, filters, filterConfigs]);

  // Set a single filter
  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Reset all filters
  const resetFilters = () => {
    const initial = {};
    filterConfigs.forEach(config => {
      initial[config.key] = config.defaultValue || 'all';
    });
    setFilters(initial);
  };

  // Reset specific filter
  const resetFilter = (key) => {
    const config = filterConfigs.find(c => c.key === key);
    setFilters(prev => ({
      ...prev,
      [key]: config?.defaultValue || 'all'
    }));
  };

  return {
    filters,
    filteredData,
    setFilter,
    resetFilters,
    resetFilter
  };
};

/**
 * Example filter configs:
 *
 * const filterConfigs = [
 *   {
 *     key: 'status',
 *     type: 'exact',
 *     accessor: (item) => item.studentStatus,
 *     defaultValue: 'all'
 *   },
 *   {
 *     key: 'trainer',
 *     type: 'exact',
 *     accessor: (item) => item.mainTrainerId,
 *     defaultValue: 'all'
 *   },
 *   {
 *     key: 'hasTrainer',
 *     type: 'boolean',
 *     accessor: (item) => !!item.mainTrainerId,
 *     defaultValue: 'all'
 *   }
 * ];
 */
